import { inBounds, indexOf } from './board.js';
import { nextInt } from './rng.js';
import { runLength } from './virus-placement.js';
/**
 * Способности игрока и атаки Versus (SPEC.md, раздел 15.2). Набор способностей уровня задаётся
 * правилом rules.abilities (слоты: 0 — «C», 1 — «V»). Каждый слот заряжается отдельно
 * за уничтоженных микробов и цепочки; при полной шкале способность можно применить.
 */
export const ABILITY_KINDS = ['cryo', 'antibiotic', 'fever', 'immunity', 'scanner'];
/** Стартовые числа — настраиваются на плейтестах. */
export const ABILITY_TUNING = Object.freeze({
    /** Энергия для применения (полная шкала слота). */
    cost: Object.freeze({ cryo: 100, antibiotic: 100, fever: 100, immunity: 60, scanner: 60 }),
    /** Энергия за шаг очистки: вирус, снятая плёнка, каждый шаг цепочки после первого. */
    energy: Object.freeze({ perVirus: 10, perShield: 5, perChainStep: 15 }),
    /** ≈ 5 с при гравитации 700 мс. */
    cryoTicks: 7,
    antibioticCount: 4,
    /** ≈ 5 с при ускоренной вдвое гравитации. */
    feverTicks: 14,
    scanCapsules: 5,
    /** Инфицирование: цепочка от ×2 отправляет min(длина, 4) микробов. */
    infectionMinChain: 2,
    infectionMax: 4,
});
export const NO_EFFECTS = Object.freeze({ frozen: 0, fever: 0, immunity: false, scan: 0 });
export function abilityCost(kind) {
    return ABILITY_TUNING.cost[kind];
}
/** Энергия за один шаг очистки. */
export function energyGain(viruses, shields, chain) {
    const e = ABILITY_TUNING.energy;
    return viruses * e.perVirus + shields * e.perShield + Math.max(0, chain - 1) * e.perChainStep;
}
/** Заряжает все слоты, не выше их полной шкалы. */
export function chargeEnergy(energy, slots, gain) {
    return slots.map((kind, i) => Math.min(abilityCost(kind), (energy[i] ?? 0) + gain));
}
/**
 * Цели «Антибиотика»: бактерии (rules.bacteriaColors), сверху вниз и слева направо,
 * не больше antibioticCount. Возвращает индексы клеток.
 */
export function antibioticTargets(board, rules) {
    const targets = [];
    board.cells.forEach((cell, i) => {
        if (cell.kind === 'virus' && rules.bacteriaColors.includes(cell.color))
            targets.push(i);
    });
    return targets.slice(0, ABILITY_TUNING.antibioticCount);
}
/**
 * «Инфицирование»: count микробов падают сверху в разные случайные колонки и встают
 * на первую опору. Колонка пропускается, если она заполнена до верха или на пути падения
 * падающая капсула (blocked). Цвет — случайный, без готовых рядов.
 */
export function dropInfection(board, rules, blocked, count, rng) {
    const landing = new Map();
    for (let x = 0; x < board.width; x++) {
        let y = -1;
        let hitsPiece = false;
        while (inBounds(board, x, y + 1) && board.cells[indexOf(board, x, y + 1)]?.kind === 'empty') {
            if (blocked.has(indexOf(board, x, y + 1))) {
                hitsPiece = true;
                break;
            }
            y++;
        }
        if (y >= 0 && !hitsPiece)
            landing.set(x, y);
    }
    const columns = [...landing.keys()];
    const cells = board.cells.slice();
    const placed = [];
    let state = rng;
    for (let k = 0; k < count && k < columns.length; k++) {
        const pick = nextInt(state, columns.length - k);
        state = pick.state;
        const j = k + pick.value;
        const x = columns[j] ?? 0;
        columns[j] = columns[k] ?? 0;
        columns[k] = x;
        const y = landing.get(x) ?? 0;
        const start = nextInt(state, rules.colorCount);
        state = start.state;
        for (let c = 0; c < rules.colorCount; c++) {
            const color = (start.value + c) % rules.colorCount;
            if (runLength(cells, board, x, y, color, 1, 0) < rules.matchLength &&
                runLength(cells, board, x, y, color, 0, 1) < rules.matchLength) {
                cells[indexOf(board, x, y)] = { kind: 'virus', color };
                placed.push({ x, y });
                break;
            }
        }
    }
    return { board: { ...board, cells }, rng: state, cells: placed };
}
//# sourceMappingURL=abilities.js.map